<script setup lang="ts">
import { Dropdown, DropdownContent, DropdownTrigger } from 'v-dropdown'

definePageMeta({
	layout: 'linktree'
})

const {params} = useRoute()
const config = useRuntimeConfig()

const { data: releaseData, error: releaseError } = await useAsyncData('release', () =>
    GqlGetRelease({ slug: params.slug.toString() })
)

const release = releaseData.value?.release

const { data: offersData, error: offersError } = await useAsyncData('offers', () =>
    GqlGetOffers()
)

const offers = offersData.value?.offers

if (release) {
  const title = `${release.artist.name}`
  const description = 'Here are all the details about ' + release.name + '. Dark and Heavy music made in Romania.'
  const route = useRoute()
  useHead({
    title: title,
    meta: [
      { name: 'description', content: description },
      { name: 'keywords', content: title },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'canonical', content: 'https://neuronoir.art' + route.path },
    ]
  })

  useSeoMeta({
    title: title,
    ogTitle: title,
    ogImage: release.image.url,
    twitterCard: release.image.url,
    description: description,
    ogDescription: description,
  })
}

function getProductPrice(productId: string) {
	let printifyOffers = offers?.filter((item: any) => item.productId == productId)
	return `€${(printifyOffers[0].price / 100).toFixed(2)}`
}

</script>

<template>
	<div :class="release?.properties.mainBackground">
		<div class="w-64 mx-auto">
			<nuxt-img :alt="release?.name" :src="release?.image.url" class="rounded" />
			<div class="flex py-5">
				<div class="flex-1">
					<nuxt-link :to="`/artists/${release?.artist.slug}`" :class="release?.properties.artistName">{{ release?.artist.name.toUpperCase() }}</nuxt-link>
					<h3 :class="release?.properties.releaseName">{{ release?.name }}</h3>
				</div>
				<div class="my-auto">
					<Dropdown>
						<!-- dropdown trigger -->
						<template #trigger>
							<button>
								<Icon size="17px" name="iconoir-menu" :class="release?.properties.menuIconColor"/>
							</button>
						</template>

            <DropdownContent>
						<div class="py-2 px-3">
							<nuxt-link :to="`/artists/${release?.artist.slug}`" class="text-sm block py-3">Artist Page</nuxt-link>
							<a target="_blank" v-for="link in release?.artist.links" :href="link.url" class="block py-3 text-sm">{{link.name}}</a>
						</div>
            </DropdownContent>
					</Dropdown>
				</div>
			</div>
			<a :class="release?.properties.linkBorder" :href="platform.url" target="_blank" v-for="platform in release?.links">
				<div class="flex-1 flex">
					<Icon size="27px" :name="platform.icon" :class="release?.properties.linkIcon"/>
					<div :class="release?.properties.linkPlatform">{{ platform.name }}</div>
				</div>
				<div>
					<Icon size="17px" name="iconoir-link" :class="release?.properties.linkUrlIcon"/>
				</div>
			</a>
			<div class="bg-white rounded p-3 mb-5" v-for="product in release?.artist.products">
				<nuxt-link :to="`/shop/${product.slug}`">
					<nuxt-img :alt="product.slug" :src="product.image.url" />
				</nuxt-link>
				<div class="my-auto text-center">
					<h4 class="font-bold">{{ product.title }}</h4>
					<h6 class="opacity-80">{{ product.type }}</h6>
					<div class="font-bold">{{ getProductPrice(product.productId) }}</div>
				</div>
				<nuxt-link :to="`/shop/${product.slug}`" class="block text-center mt-4 rounded bg-neuroprimary px-2 py-2 text-white">Add to cart</nuxt-link>
			</div>
			<div class="p-5"></div>

			<!-- Related Releases Section -->
			<section class="border-t border-gray-200 pt-4 pb-6" v-if="release?.relatedReleases.length">
				<h4 :class="`text-sm ${release?.properties.linkIcon} mb-3 px-1`">Related Releases</h4>
				<div class="flex flex-col gap-3 px-1">
					<nuxt-link 
						v-for="relatedRelease in release?.relatedReleases" 
						:key="relatedRelease.slug"
						:to="`/releases/${relatedRelease.slug}`"
						class="group flex items-center gap-3 p-2 rounded  transition-colors"
					>
						<nuxt-img 
							:src="relatedRelease.image.url" 
							:alt="relatedRelease.name"
							class="w-16 h-16 object-cover rounded"
						/>
						<div class="flex-1 min-w-0">
							<p :class="`text-xs font-medium ${release?.properties.releaseName} truncate`">{{ relatedRelease.name }}</p>
							<p :class="`text-[10px] ${release?.properties.linkIcon} truncate`">{{ relatedRelease.artist.name }}</p>
						</div>
					</nuxt-link>
				</div>
			</section>
		</div>
	</div>
</template>

<style scoped>

</style>
